import React, {
  memo, useState, useEffect, useMemo,
} from 'react'
import dynamic from 'next/dynamic'
import clsx from 'clsx'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useQueryClient } from 'react-query'
import { useIntl } from 'react-intl'
import Grid from '@mui/material/Grid2'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useSelector, useDispatch } from 'react-redux'
import {
  resetForm, resetPromoCode, resetDiscountAmount, setValue,
} from 'redux/modules/appraisal'
import usePageProps from 'hooks/usePageProps'
import { useReportMutation } from 'hooks/useReport'
import { useLocations } from 'hooks/useLocations'
import Page from 'components/layout/Page'
import isEmpty from 'lodash/isEmpty'
import { excludedBranches, isDirectSallingTypes } from 'vars'
import { isAstrollantas } from 'utils/miscellaneous'
import { getSEOTitle } from './constants'
import sx from './styles'

const TopBarDynamic = dynamic(() => import('./TopBar'))
const ModalPromotionCodeDynamic = dynamic(() => import('./Modals/ModalPromotionCode'))
const StepYearDynamic = dynamic(() => import('./Steps/Year'))
const StepMakerDynamic = dynamic(() => import('./Steps/Maker'))
const StepModelDynamic = dynamic(() => import('./Steps/Model'))
const StepModelVersionDynamic = dynamic(() => import('./Steps/ModelVersion'))
const StepVersionDynamic = dynamic(() => import('./Steps/Version'))
const MileageAndConditionDynamic = dynamic(() => import('./Steps/MileageAndCondition'))
const HistoryAndDocumentsDynamic = dynamic(() => import('./Steps/HistoryAndDocuments'))
const PersonalInformationDynamic = dynamic(() => import('./Steps/PersonalInformation'))
const OffersDynamic = dynamic(() => import('./Steps/Offers'))
const OffersEasyDirectDynamic = dynamic(() => import('./Steps/OffersEasyDirect'))
const ScheduleDynamic = dynamic(() => import('./Steps/Schedule'))
const ExpedientConfirmationDynamic = dynamic(() => import('./Steps/ExpedientConfirmation'))
const EasyConfirmationDynamic = dynamic(() => import('./Steps/ConfirmationVariant'))
const ExpedienteDigitalDynamic = dynamic(() => import('./Steps/Expediente'))
const RequestDialogDynamic = dynamic(() => import('components/VTA/RequestModal'))

function AppraisalForm() {
  const router = useRouter()
  const { formatMessage: f } = useIntl()
  const { params } = usePageProps()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { useAllInspectionLocations } = useLocations()
  const [requestDialog] = useState(null)
  const [open, setOpen] = useState(false)
  const globalForm = useSelector((s) => s.appraisal.form)
  const reportPersistedData = useSelector((s) => s.appraisal.report)
  const easyReportPersistedData = useSelector((s) => s.appraisal.easyReport)
  const { leadType } = useSelector((s) => s.appraisal)
  const personalInformation = useSelector((s) => s.appraisal.personalInformation)
  const { confirmationType } = useSelector((s) => s.common)
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const reportMutation = useReportMutation()

  const isValidPersonalInformation = useMemo(
    () => Boolean(globalForm
      && globalForm.year
      && globalForm.make
      && globalForm.model
      && globalForm.version
      && globalForm.mileage
      && params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_INFORMATION' })),
    [globalForm, params?.step],
  )

  const isValidSectionToSchedule = params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_INFORMATION' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS_DIRECT_SALLING' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS_DIRECT_SALLING_EASY' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS_EASY' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_INSPECTION' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_INSPECTION_EASY' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_INSPECTION_EASY_VARIANT' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_INSPECTION_VARIANT' })

  const isOffersStep = params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFER' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_INSPECTION_VARIANT' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS_DIRECT_SALLING' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS_DIRECT_SALLING_EASY' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS_EASY' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_INSPECTION_EASY_VARIANT' })

  const isScheduleStep = params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_INSPECTION_VARIANT' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_INSPECTION_EASY_VARIANT' })

  const isConfirmation = params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_CONFIRMATION' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_SIMPLIFIED_CONFIRMATION' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_EXPEDIENTE_CONFIRMATION' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_CONFIRMATION_EASY' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_CONFIRMATION_DIRECT' })

  const isExpediente = params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_EXPEDIENTE' })
  const initialStep = f({ id: 'ROUTE_QUOTE_CAR_STEP_YEAR' })
  const initialLink = f({ id: 'ROUTE_QUOTE_CAR' }, { step: initialStep })
  const isInitialStep = params?.step === initialStep

  const reportQuery = null

  const hideHeaderOnVTAFlow = (
    params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_INSPECTION_VARIANT' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_INSPECTION_EASY_VARIANT' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_CONFIRMATION_EASY' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_CONFIRMATION_DIRECT' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS_DIRECT_SALLING' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS_DIRECT_SALLING_EASY' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS_EASY' })
  )

  const reportData = {
    cotization: {
      version_id: globalForm?.versionId,
      mileage: globalForm?.mileage
        ? parseInt(`${globalForm.mileage}`.replace(/,/g, ''), 10)
        : '',
      requested_price: globalForm?.requested_price
        ? parseInt(`${globalForm.requested_price}`.replace(/,/g, ''), 10)
        : undefined,
      consider_trade_in: globalForm?.consider_trade_in || undefined,
      change_timeline: globalForm?.change_timeline || undefined,
      discovery_source: globalForm?.discovery_source || undefined,
      discovery_other: globalForm?.discovery_other || undefined,
    },
  }

  // const newZipcode = personalInformation?.zipcode || 77500

  // const locationsQuery = useLocationsByZipcode(
  //   newZipcode,
  //   Boolean(
  //     newZipcode
  //     || (router.query.reportId) || (
  //       newZipcode
  //       && globalForm.year
  //       && globalForm.make
  //       && globalForm.model
  //       && globalForm.version
  //       && globalForm.mileage
  //       && isValidSectionToSchedule
  //     ),
  //   ),
  // )

  // const allLocationsFormatted = locationsQuery?.data?.branches
  //   ?.filter(
  //     (branch) => !excludedBranches.some(
  //       (name) => branch.name.toLowerCase().includes(name.toLowerCase()),
  //     ),
  //   )
  //   ?.filter((option) => !(isAstrollantas(option.name)))
  //   ?.filter(
  //     (option) => !(option.name === 'A Domicilio'),
  //   )

  const locationsQuery = useAllInspectionLocations(true)

  const isMainSteps = () => params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_YEAR' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_MAKE' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_MODEL' })
    || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_VERSION' })
    || (isMobile && params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_MAKE' }))
    || (isMobile && params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_MODEL' }))
    || (isMobile && params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_VERSION' }))

  if (isInitialStep && Boolean(globalForm.year && globalForm.make)) {
    dispatch(resetForm())
  }

  const handleSubmit = (form, callback) => {
    if (leadType !== 'easy') {
      // if (callback && typeof callback === 'function') {
      //   callback()
      // }
      reportMutation.mutate(
        form,
        {
          onSuccess: ({ data }) => {
            const hasDirectSallingOffer = data?.length > 0 ? data?.find(
              (e) => isDirectSallingTypes?.includes(e?.offer_type),
            ) : null

            const easyOffer = data?.length > 0 ? data?.find(
              (e) => `${e?.offer_type}`.includes('easy'),
            ) : null

            const offer = (hasDirectSallingOffer && hasDirectSallingOffer?.show_price)
              ? hasDirectSallingOffer
              : easyOffer

            const report = offer || {}
            queryClient.setQueryData(['report', reportData], report)
            dispatch(setValue({
              key: 'offers',
              value: data,
            }))
            if (callback && typeof callback === 'function') {
              callback(report)
            }
          },
          onError: (error) => {
            // eslint-disable-next-line no-console
            console.log('error', error)
          },
        },
      )
    }
  }

  useEffect(() => {
    if (isEmpty(globalForm)
      && !globalForm?.versionId
      && params?.step !== f({ id: 'ROUTE_QUOTE_CAR_STEP_YEAR' })
      && params?.step !== f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS_DIRECT_SALLING' })
      && params?.step !== f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS_DIRECT_SALLING_EASY' })
      && params?.step !== f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS_EASY' })
      && params?.step !== f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_INSPECTION_VARIANT' })
      && params?.step !== f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_INSPECTION_EASY_VARIANT' })
      && params?.step !== f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_CONFIRMATION' })
      && params?.step !== f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_CONFIRMATION_DIRECT' })
      && params?.step !== f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_EXPEDIENTE' })
    ) {
      router.push(initialLink)
    }
  }, [params])

  useEffect(() => {
    if (isEmpty(globalForm)
      && (
        params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_INSPECTION_EASY_VARIANT' })
        || params?.step === f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_INSPECTION_VARIANT' }))
    ) {
      router.push(initialLink)
    }
  }, [params])

  useEffect(() => {
    dispatch(resetPromoCode())
    dispatch(resetDiscountAmount())
  }, [])

  useEffect(() => {
    return () => {
      dispatch(resetForm())
    }
  }, [])

  useEffect(() => {
    if (isValidPersonalInformation) {
      reportMutation.mutate(
        reportData,
        {
          onSuccess: ({ data }) => {
            const hasDirectSallingOffer = data?.length > 0 ? data?.find(
              (e) => isDirectSallingTypes?.includes(e?.offer_type) && e?.show_price,
            ) : null

            // TODO: que toma más peso un easy directo o un easy tradicional ???
            const traditionalEasyOffer = data?.length > 0 ? data?.find(
              (e) => `${e?.offer_type}`.includes('easy tradicional'),
            ) : null

            const directEasyOffer = data?.length > 0 ? data?.find(
              (e) => `${e?.offer_type}`.includes('easy directo'),
            ) : null

            let offer = null

            if (hasDirectSallingOffer) {
              offer = hasDirectSallingOffer
            } else if (traditionalEasyOffer && traditionalEasyOffer?.show_price) {
              offer = traditionalEasyOffer
            } else if (directEasyOffer && directEasyOffer?.show_price) {
              offer = directEasyOffer
            }

            const report = offer || {}

            queryClient.setQueryData(['report', reportData], report)

            dispatch(setValue({
              key: 'offers',
              value: data,
            }))
            dispatch(setValue({
              key: 'offerType',
              value: report, // => Oferta que se va mostrar
            }))
          },
          onError: (error) => {
            // eslint-disable-next-line no-console
            console.log('error', error)
          },
        },
      )
    }
  }, [isValidPersonalInformation])

  const STEPS = {
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_YEAR' })]: <StepYearDynamic
      locations={locationsQuery?.data?.branches}
    // topChildren={<RandomDynamic />}
    />,
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_MAKE' })]: <StepMakerDynamic />,
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_MAKE_EASY' })]: <StepMakerDynamic />,
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_MODEL' })]: <StepModelDynamic />,
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_MODEL_EASY' })]: <StepModelVersionDynamic />,
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_VERSION' })]: <StepVersionDynamic />,
    // [f({ id: 'ROUTE_QUOTE_CAR_STEP_COLOR' })]: <StepColorDynamic />,
    // [f({ id: 'ROUTE_QUOTE_CAR_STEP_COLOR_EASY' })]: <StepColorDynamic />,
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_KILOMETERS' })]: <MileageAndConditionDynamic />,
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_PRICE' })]: <MileageAndConditionDynamic />,
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_HISTORY_DOCS' })]: <HistoryAndDocumentsDynamic />,
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_INFORMATION' })]: (
      <PersonalInformationDynamic
        report={reportQuery}
        onSubmit={handleSubmit}
        isValidZipcode={locationsQuery?.data?.branches}
        locations={locationsQuery?.data?.branches}
        offers={reportMutation?.data?.data}
      />
    ),
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_INFORMATION_EASY' })]: (
      <PersonalInformationDynamic
        report={reportQuery}
        onSubmit={handleSubmit}
        isValidZipcode={locationsQuery?.data?.branches}
        locations={locationsQuery?.data?.branches}
        offers={reportMutation?.data?.data}
      />
    ),
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS_DIRECT_SALLING' })]: (
      <OffersDynamic />
    ),
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS_DIRECT_SALLING_EASY' })]: (
      <OffersEasyDirectDynamic />
    ),
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_OFFERS_EASY' })]: (
      <OffersDynamic />
    ),
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_INSPECTION_VARIANT' })]: (
      <ScheduleDynamic
        report={reportQuery}
        locations={locationsQuery}
        onSubmit={handleSubmit}
      />
    ),
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_INSPECTION_EASY_VARIANT' })]: (
      <ScheduleDynamic
        report={reportQuery}
        locations={locationsQuery}
        onSubmit={handleSubmit}
      />
    ),
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_CONFIRMATION' })]: (
      <ExpedientConfirmationDynamic
        report={reportQuery || reportPersistedData}
        confirmationType={confirmationType}
        onNoneData={() => router.push(initialLink)}
      />
    ),
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_CONFIRMATION_EASY' })]: (
      <EasyConfirmationDynamic
        report={easyReportPersistedData}
        onNoneData={() => router.push(initialLink)}
      />
    ),
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_CONFIRMATION_DIRECT' })]: (
      <EasyConfirmationDynamic
        report={easyReportPersistedData}
        onNoneData={() => router.push(initialLink)}
      />
    ),
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_EXPEDIENTE' })]: (
      <ExpedienteDigitalDynamic
        report={reportQuery || reportPersistedData}
        onNoneData={() => router.push(initialLink)}
      />
    ),
    [f({ id: 'ROUTE_QUOTE_CAR_STEP_SCHEDULE_EXPEDIENTE_CONFIRMATION' })]: (
      <ExpedientConfirmationDynamic
        report={reportQuery || reportPersistedData}
        confirmationType={confirmationType}
        onNoneData={() => router.push(initialLink)}
      />
    ),
  }

  const getTopBar = () => {
    if (!isConfirmation && !isOffersStep && !isExpediente) {
      return (
        <TopBarDynamic
          isOfferStep={isOffersStep}
          showInitialSummary={isInitialStep}
        />
      )
    }
    return null
  }

  const toggleOpenModal = () => setOpen((prev) => !prev)

  return (
    <>
      <Head>
        <meta
          name="robots"
          content="noindex, nofollow"
        />
      </Head>
      <Head>
        <title>
          {[f({ id: 'QUOTE_YOUR_CAR_TITLE' }), getSEOTitle(params?.step)].join(' | ')}
        </title>
      </Head>
      <Page
        variant="bg-grey"
        isMainSteps={isMainSteps()}
      >
        <Grid
          container
          id="form-content"
          sx={sx.container}
          className={clsx({
            isScheduleStep,
          })}
        >
          <Grid
            size={12}
            container
            justifyContent="center"
            sx={sx.main}
            className={clsx({
              hideHeader: hideHeaderOnVTAFlow,
            })}
          >
            {getTopBar()}
            <Grid size={12}>
              {STEPS[params?.step]}
            </Grid>
          </Grid>

          {requestDialog !== null && (
            <RequestDialogDynamic
              open={requestDialog !== null}
              {...requestDialog}
            />
          )}
          <ModalPromotionCodeDynamic
            open={open}
            handleClose={toggleOpenModal}
          />
        </Grid>
      </Page>
    </>
  )
}

export default memo(AppraisalForm)
